import React, { useState, useCallback, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import '../../styles/flow-animations.css';
import {
  PlusIcon,
  EyeIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ArrowLeftIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { FormTemplate, FormField, FormSection, FormBuilderState } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import { TemplateService } from '../../services/templateService';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import FieldPalette from './FieldPalette';
import FormCanvas from './FormCanvas';
import FieldProperties from './FieldProperties';
import FormPreview from './FormPreview';
import FormSettings from './FormSettings';
import ImportModal from './ImportModal';

interface FormBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: FormTemplate) => Promise<void>;
  initialTemplate?: FormTemplate;
  useFullPage?: boolean;
}

const FormBuilder: React.FC<FormBuilderProps> = ({
  isOpen,
  onClose,
  onSave,
  initialTemplate,
  useFullPage = false
}) => {
  const { t } = useLanguage();
  const [state, setState] = useState<FormBuilderState>({
    template: initialTemplate || createEmptyTemplate(),
    previewMode: false
  });
  
  const [showSettings, setShowSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'warning';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });

  const [importModal, setImportModal] = useState<{
    isOpen: boolean;
    templates: Partial<FormTemplate>[];
    isMultiple: boolean;
  }>({
    isOpen: false,
    templates: [],
    isMultiple: false
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update internal state when initialTemplate changes
  useEffect(() => {
    if (initialTemplate) {
      setState(prev => ({
        ...prev,
        template: initialTemplate
      }));
    }
  }, [initialTemplate]);

  // Sidebar toggle function
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Properties toggle function
  const toggleProperties = () => {
    setIsPropertiesOpen(!isPropertiesOpen);
  };

  // Auto-open properties on mobile when field is selected
  useEffect(() => {
    if (state.selectedField && useFullPage) {
      // On mobile, auto-open properties panel when field is selected
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        setIsPropertiesOpen(true);
      }
    }
  }, [state.selectedField, useFullPage]);

  // Auto-dismiss notification after 5 seconds
  useEffect(() => {
    if (notification.isOpen) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, isOpen: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.isOpen]);

  const updateTemplate = useCallback((updates: Partial<FormTemplate>) => {
    setState(prev => ({
      ...prev,
      template: { ...prev.template, ...updates }
    }));
  }, []);

  const addField = useCallback((field: FormField, sectionId?: string) => {
    const newField = {
      ...field,
      id: `field_${Date.now()}`,
      order: state.template.fields.length,
      section: sectionId || field.section // Use provided sectionId or field's existing section
    };
    
    updateTemplate({
      fields: [...state.template.fields, newField]
    });
  }, [state.template.fields, updateTemplate]);

  const updateField = useCallback((fieldId: string, updates: Partial<FormField>) => {
    updateTemplate({
      fields: state.template.fields.map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    });
  }, [state.template.fields, updateTemplate]);

  const deleteField = useCallback((fieldId: string) => {
    updateTemplate({
      fields: state.template.fields.filter(field => field.id !== fieldId)
    });
    setState(prev => ({
      ...prev,
      selectedField: prev.selectedField === fieldId ? undefined : prev.selectedField
    }));
  }, [state.template.fields, updateTemplate]);

  const addSection = useCallback((section: FormSection) => {
    const newSection = {
      ...section,
      id: section.id || `section_${Date.now()}`,
      order: section.order !== undefined ? section.order : state.template.sections.length
    };
    
    updateTemplate({
      sections: [...state.template.sections, newSection]
    });
  }, [state.template.sections, updateTemplate]);

  const updateSection = useCallback((sectionId: string, updates: Partial<FormSection>) => {
    updateTemplate({
      sections: state.template.sections.map(section =>
        section.id === sectionId ? { ...section, ...updates } : section
      )
    });
  }, [state.template.sections, updateTemplate]);

  const deleteSection = useCallback((sectionId: string) => {
    // Also remove all fields in this section
    updateTemplate({
      sections: state.template.sections.filter(section => section.id !== sectionId),
      fields: state.template.fields.filter(field => field.section !== sectionId)
    });
  }, [state.template.sections, state.template.fields, updateTemplate]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        ...state.template,
        updatedAt: new Date(),
        version: (state.template.version || 0) + 1
      });
      // Only close if not in full page mode (i.e., in modal mode)
      if (!useFullPage) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving form template:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const togglePreview = () => {
    setState(prev => ({ ...prev, previewMode: !prev.previewMode }));
  };

  // Form template exportálása
  const onExportTemplate = useCallback(() => {
    if (!state.template) return;
    
    const exportData = TemplateService.exportFormTemplate(state.template);
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${state.template.name || 'form-template'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Exportálás sikeres',
      message: 'Az űrlap sablon sikeresen exportálva lett.'
    });
  }, [state.template]);

  // Form template importálása
  const onImportTemplate = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = e.target?.result as string;
        const importedTemplates = TemplateService.importMultipleFormTemplates(jsonData);
        
        // Open import modal with the templates
        setImportModal({
          isOpen: true,
          templates: importedTemplates,
          isMultiple: importedTemplates.length > 1
        });
      } catch (error) {
        console.error('Error importing form template:', error);
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Importálási hiba',
          message: `Hiba történt az űrlap sablon importálása során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
        });
      }
    };
    reader.readAsText(file);
    
    // Reset input
    event.target.value = '';
  }, []);

  // Import modal bezárása
  const handleCloseImportModal = () => {
    setImportModal({
      isOpen: false,
      templates: [],
      isMultiple: false
    });
  };

  // Import végrehajtása a kiválasztott szervezetbe
  const handleConfirmImport = async (templates: Partial<FormTemplate>[], organizationId: string, addSuffix: boolean = true) => {
    try {
      // FormBuilder esetében csak az első sablont használjuk
      const importedTemplate = templates[0];
      
      let templateName = importedTemplate.name;
      
      // Tisztítjuk az "(importált)" és "(importált 1)", "(importált 2)" stb. szövegeket
      if (templateName) {
        templateName = templateName.replace(/\s*\(importált\s*\d*\)\s*/g, '').trim();
      }
      
      // Csak akkor adjuk hozzá az (importált) utótagot, ha a felhasználó kéri
      if (addSuffix && templateName) {
        templateName = `${templateName} (importált)`;
      }
      
      setState(prev => ({
        ...prev,
        template: {
          ...createEmptyTemplate(),
          ...importedTemplate,
          name: templateName,
          id: '', // Reset ID for new template
          organizationId: organizationId,
          createdBy: '', // Will be set when saving
          createdAt: new Date(),
          updatedAt: new Date()
        } as FormTemplate
      }));
      
      setNotification({
        isOpen: true,
        type: 'success',
        title: 'Importálás sikeres',
        message: 'Az űrlap sablon sikeresen importálva lett.'
      });
      
      console.log('Form template imported:', importedTemplate);
    } catch (error) {
      console.error('Error importing form template:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Importálási hiba',
        message: `Hiba történt az importálás során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
      });
    }
  };

  if (!isOpen) return null;

  // Full page layout (without modal)
  if (useFullPage) {
    const selectedFieldData = state.selectedField ? state.template.fields.find(f => f.id === state.selectedField) : undefined;

    return (
      <DndProvider backend={HTML5Backend}>
        <div className="flex-1 flex overflow-hidden h-full">
          {state.previewMode ? (
            <div className="flex-1 flex flex-col">
              {/* Preview Toolbar */}
              <div className="flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('formBuilder.preview')} - {state.template.name || t('formBuilder.untitledForm')}
                  </h2>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={togglePreview}
                    className="flex items-center"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    {t('common.edit') || 'Edit'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onExportTemplate}
                    className="flex items-center"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Exportálás
                  </Button>
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      onChange={onImportTemplate}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                      Importálás
                    </button>
                  </>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    className="flex items-center"
                  >
                    <Cog6ToothIcon className="h-4 w-4 mr-2" />
                    {t('formBuilder.settings')}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                        {t('common.save')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {/* Preview Content */}
              <div className="flex-1 overflow-y-auto">
                <FormPreview template={state.template} />
              </div>
            </div>
          ) : (
            <>
              {/* Left Sidebar - Field Palette - Responsive */}
              <div className={`
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                md:translate-x-0 fixed inset-y-0 left-0 z-40 md:relative md:inset-y-auto md:left-auto md:z-auto
                w-72 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-full
                transition-transform duration-300 ease-in-out
              `}>
                <FieldPalette onAddField={addField} />
              </div>

              {/* Mobile Sidebar Overlay */}
              {isSidebarOpen && (
                <div
                  className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
                  onClick={toggleSidebar}
                />
              )}

              {/* Main Canvas */}
              <div className="flex-1 bg-white dark:bg-gray-900 h-full flex flex-col relative">
                {/* Mobile Sidebar Indicator - Purple stripe on left */}
                <div className={`
                  md:hidden absolute left-0 top-1/2 -translate-y-1/2 z-[9998]
                  w-3 h-24 bg-gradient-to-b from-purple-500 to-blue-500 rounded-r-lg shadow-lg
                  transition-all duration-300 cursor-pointer
                  ${isSidebarOpen ? 'opacity-0 -translate-x-4' : 'opacity-80 hover:opacity-100 hover:w-4 hover:shadow-xl'}
                `}
                  onClick={toggleSidebar}
                  title={t('formBuilder.fieldPalette') || 'Field Palette'}
                />

                {/* Toolbar */}
                <div className="flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-2">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {state.template.name || t('formBuilder.untitledForm')}
                    </h2>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={togglePreview}
                      className="flex items-center"
                    >
                      <EyeIcon className="h-4 w-4 mr-2" />
                      {state.previewMode ? t('formBuilder.forms') : t('formBuilder.preview')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={onExportTemplate}
                      className="flex items-center"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                      Exportálás
                    </Button>
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json"
                        onChange={onImportTemplate}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                        Importálás
                      </button>
                    </>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowSettings(true)}
                      className="flex items-center"
                    >
                      <Cog6ToothIcon className="h-4 w-4 mr-2" />
                      {t('formBuilder.settings')}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSave}
                      disabled={isSaving}
                      className="flex items-center"
                    >
                      {isSaving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {t('common.saving')}
                        </>
                      ) : (
                        <>
                          <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                          {t('common.save')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <FormCanvas
                    template={state.template}
                    selectedField={state.selectedField}
                    onSelectField={(fieldId) => setState(prev => ({ ...prev, selectedField: fieldId }))}
                    onUpdateField={updateField}
                    onDeleteField={deleteField}
                    onAddSection={addSection}
                    onAddField={addField}
                    onUpdateSection={updateSection}
                    onDeleteSection={deleteSection}
                    onUpdateTemplate={updateTemplate}
                  />
                </div>
              </div>

              {/* Properties Panel - Desktop: sidebar, Mobile: bottom modal */}
              {selectedFieldData && (
                <>
                  {/* Desktop Properties Panel */}
                  <div className="hidden md:block w-96 flex-shrink-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 h-full overflow-y-auto">
                    <FieldProperties
                      field={selectedFieldData}
                      onUpdateField={updateField}
                    />
                  </div>

                  {/* Mobile Properties Modal */}
                  <div 
                    className="md:hidden fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-end"
                    onClick={() => {
                      setState(prev => ({ ...prev, selectedField: undefined }));
                      setIsPropertiesOpen(false);
                    }}
                  >
                    <div 
                      className="w-full bg-white dark:bg-gray-800 rounded-t-xl max-h-[80vh] overflow-hidden animate-slide-up"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Mobile Modal Handle */}
                      <div className="flex justify-center py-2 border-b border-gray-200 dark:border-gray-700">
                        <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                      </div>
                      
                      <div className="overflow-y-auto max-h-[calc(80vh-3rem)]">
                        <FieldProperties
                          field={selectedFieldData}
                          onUpdateField={updateField}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Settings Modal */}
        <FormSettings
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          template={state.template}
          onUpdateTemplate={updateTemplate}
        />

        {/* Notification */}
        {notification.isOpen && (
          <div className="fixed top-4 right-4 z-50">
            <div className={`
              px-4 py-3 rounded-lg shadow-lg max-w-sm
              ${notification.type === 'success' ? 'bg-green-100 border border-green-400 text-green-700' : ''}
              ${notification.type === 'error' ? 'bg-red-100 border border-red-400 text-red-700' : ''}
              ${notification.type === 'warning' ? 'bg-yellow-100 border border-yellow-400 text-yellow-700' : ''}
            `}>
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold">{notification.title}</h4>
                  <p className="text-sm mt-1">{notification.message}</p>
                </div>
                <button
                  onClick={() => setNotification(prev => ({ ...prev, isOpen: false }))}
                  className="ml-4 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        <ImportModal
          isOpen={importModal.isOpen}
          onClose={handleCloseImportModal}
          onImport={handleConfirmImport}
          importedTemplates={importModal.templates}
          isMultiple={importModal.isMultiple}
        />
      </DndProvider>
    );
  }

  // Original modal layout
  return (
    <DndProvider backend={HTML5Backend}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={state.previewMode ? t('formBuilder.preview') : t('formBuilder.title')}
        size="full"
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center space-x-4">
              {state.previewMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={togglePreview}
                  className="flex items-center"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              )}
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {state.template.name || t('formBuilder.untitledForm')}
              </h2>
            </div>
            
            <div className="flex items-center space-x-2">
              {!state.previewMode && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    className="flex items-center"
                  >
                    <Cog6ToothIcon className="h-4 w-4 mr-2" />
                    {t('formBuilder.settings')}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={togglePreview}
                    className="flex items-center"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    {t('formBuilder.preview')}
                  </Button>
                </>
              )}
              
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center"
              >
                {isSaving ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex h-full">
            {state.previewMode ? (
              <FormPreview template={state.template} />
            ) : (
              <>
                {/* Left Sidebar - Field Palette (Sticky) */}
                <div className="w-72 border-r border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex-shrink-0 sticky top-0 h-screen overflow-y-auto">
                  <FieldPalette onAddField={addField} />
                </div>

                {/* Main Canvas */}
                <div className="flex-1 bg-white dark:bg-gray-900 overflow-y-auto scroll-smooth">
                  <FormCanvas
                    template={state.template}
                    selectedField={state.selectedField}
                    onSelectField={(fieldId) => setState(prev => ({ ...prev, selectedField: fieldId }))}
                    onUpdateField={updateField}
                    onDeleteField={deleteField}
                    onAddSection={addSection}
                    onAddField={addField}
                    onUpdateSection={updateSection}
                    onDeleteSection={deleteSection}
                    onUpdateTemplate={updateTemplate}
                  />
                </div>

                {/* Right Sidebar - Properties (Sticky) */}
                <div className="w-96 border-l border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex-shrink-0 sticky top-0 h-screen overflow-y-auto">
                  <FieldProperties
                    field={state.selectedField ? state.template.fields.find(f => f.id === state.selectedField) : undefined}
                    onUpdateField={updateField}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Settings Modal */}
        <FormSettings
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          template={state.template}
          onUpdateTemplate={updateTemplate}
        />

        {/* Notification */}
        {notification.isOpen && (
          <div className="fixed top-4 right-4 z-50">
            <div className={`
              px-4 py-3 rounded-lg shadow-lg max-w-sm
              ${notification.type === 'success' ? 'bg-green-100 border border-green-400 text-green-700' : ''}
              ${notification.type === 'error' ? 'bg-red-100 border border-red-400 text-red-700' : ''}
              ${notification.type === 'warning' ? 'bg-yellow-100 border border-yellow-400 text-yellow-700' : ''}
            `}>
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold">{notification.title}</h4>
                  <p className="text-sm mt-1">{notification.message}</p>
                </div>
                <button
                  onClick={() => setNotification(prev => ({ ...prev, isOpen: false }))}
                  className="ml-4 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        <ImportModal
          isOpen={importModal.isOpen}
          onClose={handleCloseImportModal}
          onImport={handleConfirmImport}
          importedTemplates={importModal.templates}
          isMultiple={importModal.isMultiple}
        />
      </Modal>
    </DndProvider>
  );
};

// Helper function to create empty template
function createEmptyTemplate(): FormTemplate {
  return {
    id: '',
    name: '',
    description: '',
    category: '',
    tags: [],
    fields: [],
    sections: [],
    settings: {
      submitButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      showProgressBar: false,
      allowDrafts: true,
      multiStep: false
    },
    permissions: {
      canView: [],
      canEdit: [],
      canSubmit: []
    },
    organizationId: '',
    createdBy: '',
    createdAt: new Date(),
    updatedAt: new Date(),
    version: 1,
    isActive: true
  };
}

export default FormBuilder;
